import { useState, useCallback } from 'react';

export function useEditableGrid({ isValid, emptyRow, globalRows = false }) {
    const [gridApi, setGridApi] = useState(null);
    const [hasError, setHasError] = useState(false);

    const rowStyle = { background: 'white' };

    // No setHasError in render phase — callers invoke checkErrors from onCellValueChanged
    const getRowStyle = useCallback((params) => {
        if (params.node.rowIndex === params.api.rowModel.rowsToDisplay.length - 1) {
            return { background: '#d2f6fac7' };
        }
        if (!isValid(params.node.data)) {
            return { background: '#ffb09c' };
        }
        if (globalRows && params.node.data.global) {
            return { background: '#eee' };
        }
        return { background: 'white' };
    }, [isValid, globalRows]);

    // Call from onCellValueChanged to update hasError without touching the render phase
    const checkErrors = useCallback((api) => {
        if (!api) return;
        const items = [];
        api.forEachNode(node => items.push(node.data));
        items.pop();
        setHasError(items.some(data => !isValid(data)));
    }, [isValid]);

    // Sets gridApi and populates the grid with initialData (or empty) plus one empty row
    const initGrid = useCallback((params, initialData) => {
        setGridApi(params.api);
        const data = initialData != null ? [...initialData] : [];
        data.push({ ...emptyRow });
        params.api.setRowData(data);
    }, [emptyRow]);

    // Appends a new empty row and scrolls to it
    const addRow = useCallback((api) => {
        if (!api) return;
        api.applyTransaction({ add: [{ ...emptyRow }] });
        api.ensureIndexVisible(api.getDisplayedRowCount() - 1, 'top');
    }, [emptyRow]);

    // Returns all rows except the trailing empty row
    const getItems = useCallback((api) => {
        const _api = api || gridApi;
        const items = [];
        _api.forEachNode(node => items.push(node.data));
        items.pop();
        return items;
    }, [gridApi]);

    // Replaces grid content with data + a trailing empty row
    const setItems = useCallback((data, api) => {
        const _api = api || gridApi;
        const newData = data != null ? [...data, { ...emptyRow }] : [{ ...emptyRow }];
        _api.setRowData(newData);
    }, [gridApi, emptyRow]);

    // Removes a row that has no server id (local-only delete)
    const deleteRowLocally = useCallback((params) => {
        const rowData = [];
        params.api.forEachNode(node => rowData.push(node.data));
        rowData.splice(params.rowIndex, 1);
        params.api.setRowData(rowData);
    }, []);

    return {
        gridApi,
        setGridApi,
        hasError,
        setHasError,
        rowStyle,
        getRowStyle,
        checkErrors,
        initGrid,
        addRow,
        getItems,
        setItems,
        deleteRowLocally,
    };
}
